import type {
  AdBlockSize,
  AdBreakType,
  AssetTransitionType,
  ChannelType,
  ConformStatus,
  LiveJoinInProgress,
  LiveScheduleTransitionTypes,
} from '../enums';
import type { Category, Dictionary, Item, OrganizationalUnit, ThumbnailResourceData } from '.';
import type { ExternalServiceMeta } from './item';
import type { ModelBase, OUModelBase } from './modelBase';
import type { ChannelRefreshControl, WorkflowInfo } from './Ou';

export type SourceType = 'HLS Source' | 'RTMP Source';

export type SortType = 'asc' | 'desc';
export type RangeType = 'None' | 'Current' | 'Prior';
export type Range = 'Day' | 'Week' | 'Month';

export interface LiveProgramMetadata {
  invertSCTE?: boolean;
  sourceType?: SourceType | null;
  categories: string[] | Category[];
  tags: string[];
  autoCreateClipGroup: boolean;
  thumbnail: ThumbnailResourceData;
  autoCreateClipGroupTags: string[];
  scheduleId: string;
  scheduleICSData: string;
}

export interface LiveProgram extends OUModelBase {
  name?: string;
  channels?: Channel[];
  enabled?: boolean;
  hasGeofencing?: boolean;
  zipcodes?: string;
  timezone?: string;
  streamingEngineId?: string;
  // the SCTE codes come in and tell us when to start/stop encoding
  hasSCTE?: boolean;
  recordForReplay?: boolean;
  isExternalSource?: boolean;
  sourceUrl?: string;
  ouName?: string;
  startupProgramClipId?: string;
  isCutToLive?: boolean;
  metadata?: LiveProgramMetadata;
  isDeleted?: boolean;
  streamIdentifier?: string;
  description: string;
  scheduleEnabled?: boolean;
}

export interface TransitionTemplate extends OUModelBase {
  name: string;
  enabled: boolean;
  transitionIn?: string;
  transitionOut?: string;
  transitionInAd?: string;
  transitionOutAd?: string;
  interstitial?: string;
  automatedTitle?: string;
  isDeleted?: boolean;
}

export interface ContentPool extends OUModelBase {
  name?: string;
  items: Item[];
  streamingEngineId?: string;
  streamingEngineFillerId?: string;
}

export interface ChannelContentPool extends ContentPool {
  weight: number;
}

export interface ChannelAttribute {
  key: string;
  name: string;
  score?: number;
  type?: string;
  isMeta?: boolean;
}

export interface ChannelAttributePool {
  sys?: ChannelAttribute[];
  tag?: ChannelAttribute[];
  excludedTags?: ChannelAttribute[];
  cat?: ChannelAttribute[];
  excludedCats?: ChannelAttribute[];
  createdDateSort?: SortType;
  rangeType?: RangeType;
  range?: Range;
  rangeDuration?: string;
  prefixSortOrder?: TagPrefixSortInfo[];
}

export interface AdConfiguration {
  id?: string;
  spotxId: string;
  initTime: number; // in sec
  loadTime: number; // in sec
  blockSize: AdBlockSize;
  singleTime?: number;
  podTime?: number;
  overrideLive: boolean;
  liveAdMaxTime?: number;
  liveAdMinTime?: number;
  primaryDomainUrl: string;
  adBreakType?: AdBreakType;
  adsPerHour?: number;
}

export interface NonstopNewsTransitionSettings {
  beginMomentarilyItemId: string;
  startupLiveItemId?: string;
  startupRecordedItemId?: string;
  preRecordedToLiveItemId: string;
  adFillerItemId: string;
  switchToMostRecentItemId: string;
}

export interface LiveOnlyTransitionSettings {
  preLiveItemId: string;
  postLiveItemId: string;
  startupLiveItemId?: string;
  contentRestrictedItemId?: string;
  untilTime: Date;
}

export interface CompactChannel extends ModelBase {
  name: string;
  description: string;
  type: string;
  enabled: boolean;
  deleted: boolean;
  url: string;
  streamingEngineId?: string;

  // ui props for display
  stream?: any; // the first stream from stream events that matches
}

export interface Channel extends ModelBase {
  id: string;
  name: string;
  alias: string;
  url: string;
  type: ChannelType;
  friendlyChannelTypeName?: string;
  liveScheduleId?: string;
  extendedType?: string;
  contentPools?: ChannelContentPool[];
  attributes?: ChannelAttributePool;
  enabled: boolean;
  deleted: boolean;
  halted: boolean;
  createdDate: string;
  description: string;
  sessions?: number; // number of active sessions
  channelIdOnDepletion?: string; // if this id is null, the channel will repeat,
  streamingEngineId?: string;
  showAds: boolean;
  liveScheduleTransitionType: LiveScheduleTransitionTypes;
  // liveScheduleConfigurationId?: string; // live schedule id
  liveScheduleEnabled?: boolean;
  liveScheduleProgramIds: string[]; // list of live program Ids for saving and retrieval to/from db.
  liveSchedulePrograms?: ChannelLiveProgram[]; // list for UI binding hydrated on the client side from the liveScheduleProgramIds
  nonstopNewsTransitionSettings: NonstopNewsTransitionSettings;
  liveOnlyTransitionSettings: LiveOnlyTransitionSettings;
  children: Channel[];
  linkedChannels: Channel[];
  autoSort?: boolean;
  transitionEnabled?: boolean;
  transitionTemplateId?: string;
  prioritizeUnwatchedContent?: boolean;
  liveJoinInProgress: LiveJoinInProgress;
  includeBreakingNews?: boolean;
  timezone?: string;
  metaData?: ChannelMetaData;
  privateStorageDirectory?: string;
  publicStorageDirectory?: string;

  actions?: any[];
  isChannelNested?: boolean;
  enableEPGGeneration?: boolean;
  clipIds?: string[];
  ouName?: string;
  mrssUrl?: string;
  thumbnailSrc?: string;
  isLinkedChannel?: boolean;
  isParentChannel?: boolean;
  isSelectable?: boolean;

  // UI Properties for Series Container
  tmp?: Temp;
  originalSeries?: Series | null;
  originalSeasons?: Season[];
  originalEpisodeIds?: string[];
  originalAlias?: string | null;
  enableAliasFeature?: boolean;
}

export interface Temp {
  itemLookup?: Dictionary<Item>;
}

export interface Season extends Channel {
  episodes?: Item[];
}
export interface Series extends Channel {
  seasons?: Season[];
}
// "metaData": {
//   "variantCodes": [
//     "600K360AWS",
//     "3500K720AWS"
//   ],
//   "liveFillers": {
//     "beginMomentarilyItemId": "6fccc6c8-fa4a-4105-9f66-88185a89e949",
//     "startupLiveItemId": "5703a63c-f429-4d9c-95cd-da6ed23b801d",
//     "startupRecordedItemId": "fcefb378-6bf2-4a08-ac3d-36077c5a75cf",
//     "preRecordedToLiveItemId": "a29fd9b3-dbe8-491a-8322-788e49dfa73f",
//     "switchToMostRecentItemId": "8fd592c4-8724-4758-81c2-084c318a02cc",
//     "adFillerItemId": null,
//     "contentRestrictedItemId": "1dc91f18-1c69-48c6-b040-c7b5e386f9e3",
//     "createdBy": "3d092eda-1be9-48bf-92a4-f0d9d5effebb"
//   },
//   "adFillers": {
//     "itemSEClipIds": [
//       "3184021f-c6b6-4f15-a554-210e7d492ef8",
//       "06def33f-bcc4-4e92-a444-c63c7d4b9aac"
//     ]
//   }
export interface ChannelMetaData {
  channelSubtype: ChannelSubtype;
  extendedType?: string;
  modifiedDate?: string;
  randomizePlaylist: boolean;
  isBroadcastChannel: boolean;
  isLinearChannel: boolean;
  variantCodes: string[];
  enableExternalSubtitle: boolean;
  fillers?: ChannelLiveFillers;
  targetDuration: number;
  epgConfig: EpgConfig;
  refreshControl: ChannelRefreshControl | null;
  geoFencing?: GeoFencing;
  nestedChannelConfig?: NestedChannelConfig;
  channelContentLimits?: ChannelContentLimits;
  thumbnail?: ThumbnailResourceData;
  externalServiceMetas?: ExternalServiceMeta[];
  adConfiguration?: AdConfiguration;
  linkedChannelInfo?: LinkedChannelSettings;
  liveProgramConfig?: LiveProgramConfig;
  graphicsOverlayConfig?: GraphicsOverlayConfig;
  includeClipMetadata?: boolean;
  isPinned?: boolean;
  disableCC?: boolean;
  linkedEntitiesConfig?: LinkedEntitiesConfig;
  linkedAssets?: LinkedAssetInfo[];
  itemLookup?: Dictionary<Item>;
  channelBusinessMetadata?: CommonBusinessMetadata;

  // Shared Settings
  accessSettings?: AccessSettings;
  displaySettings?: DisplaySettings;
  publicationStatus?: PublicationStatus;

  workflowInfo?: WorkflowInfo;
  externalChannelInfo?: ExternalChannelInfo;
}

export interface ExternalChannelInfo {
  externalUrl?: string;
  externalEPGUrl?: string;
}

export interface LinkedEntitiesConfig {
  linkedImages?: Dictionary<string>;
}

export interface PublicationStatus {
  status: PublicationStatusType;
  // passwordProtected: boolean;
}

export interface DisplaySettings {
  backgroundColor: string;

  // UI Properties that map to LinkedAssets
  favIcon?: string;
  thumbnail?: string;
  poster?: string;
  background?: string;
  logo?: string;
  badge?: string;
}

export interface AccessSettings {
  accessLevel: AccessLevelType;
  effectiveAccessLevel: AccessLevelType;
  geographic?: GeographicRestrictions;
  demographic?: DemographicRestrictions;
  temporal?: TemporalRestrictions;
  effectiveEmbargoDate?: string;
  effectiveExpireDate?: string;
}
export interface GeographicRestrictions {
  geoblockingType: GeographicRestrictionType;
  countries: string[];
}
export interface DemographicRestrictions {
  ageGate: number;
}
export interface TemporalRestrictions {
  accessSchedules: AccessSchedule[];
}
export interface AccessSchedule {
  from: string;
  to: string;
  accessLevel: AccessLevelType;

  // UI props for displaying active and controlling access schedule
  isActive?: boolean;
  isControlling?: boolean;
}

export enum AccessLevelType {
  Free = 'Free',
  Paywall = 'Paywall',
  Private = 'Private',
}

export enum GeographicRestrictionType {
  NoGeoblocking = 'NoGeoblocking',
  AllowInTheFollowingCountries = 'AllowInTheFollowingCountries',
  BlockInTheFollowingCountries = 'BlockInTheFollowingCountries',
}

export enum PublicationStatusType {
  Unknown = 'Unknown', // Do not expose to consumers, this is the default in the system for existing assets
  Draft = 'Draft',
  Pending = 'Pending',
  Private = 'Private',
  Scheduled = 'Scheduled',
  Published = 'Published',
}

// *
// //   public string Description { get; set; }
// 		public string HighlightMessage { get; set; }
// 		public List<string> Cast { get; set; }
// 		[Description("aka Creators")]
// 		public List<string> Producers { get; set; }
// 		public List<string> Directors { get; set; }
// 		public string MpaaRating { get; set; }
// 		public string BbfcRating { get; set; }
// 		public DateTime? PublicationDate { get; set; }
// 		public string ReleaseYear { get; set; }
// 		public List<string> Tags { get; set; }
// 		public List<string> Categories { get; set; }
// 		public List<string> Genres { get; set; }
// */

export interface CommonTagKeys {
  cast: string[]; // 'actor' or 'cast'
  producer: string[]; // aka 'Creator'
  director: string[];
  ratingMpaa: string[];
  ratingBbfc: string[];
  releaseYear: string | null;
  episodeXmltv: string | null;
  episodeOnscreen: string | null;
  usParentalRatings: string | null;
  europeanFskRating: string | number | null;
  networkValue: string | null;
  programType: string | null;
  originalAirDate: string | null;
  teamName: string[] | [];
  leagueName: string[] | [];
  tmsidEpisode: string | null;
  tmsidSeries: string | null;
  genre: string[];
  tags: string[]; // all tags not collected by the above meta keys
  season: string[] | [];
  episode: string | null;
  series: string[] | [];
}

export interface CommonBusinessMetadata {
  description?: string;
  highlightMessage?: string;
  cast?: string[];
  producer?: string[];
  director?: string[];
  mpaaRating?: string;
  bbfcRating?: string;
  publicationDate?: string | Date;
  releaseYear?: string;
  tags?: string[];
  categories?: string[]; // a list of category names only (possibility of corruption if ou category names change)
  genre?: string[];
  episodeXmltv?: string;
  episodeOnscreen?: string | null;
  usParentalRatings?: string | null;
  europeanFskRating?: string | null;
  networkValue?: string | null;
  programType?: string | null;
  originalAirDate?: string | null;
  teamName?: string[] | null;
  leagueName?: string[] | null;
  tmsidEpisode?: string | null;
  tmsidSeries?: string | null;
  season?: string[] | null;
  episode?: string | null;
  series?: string[] | null;
}

// public enum LinkedAssetType
// {
// 	Unknown = 0,
// 	FavIcon,
// 	Thumbnail,
// 	Poster,
// 	Background,
// 	Logo,
// 	Badge,
// 	Extra,
// 	Trailer,
// }

export enum LinkedAssetType {
  Unknown = 'Unknown',
  FavIcon = 'FavIcon',
  Thumbnail = 'Thumbnail',
  Poster = 'Poster',
  Background = 'Background',
  Logo = 'Logo',
  Badge = 'Badge',
  Extra = 'Extra',
  Trailer = 'Trailer',
}
export interface LinkedAssetInfo {
  assetId: string;
  type: LinkedAssetType;
}
export enum ChannelSubtype {
  Unknown = 'Unknown',
  None = 'None',
  Broadcast = 'Broadcast',
  Linear = 'Linear',
  Collection = 'Collection',
  Series = 'Series',
  Season = 'Season',
  Extra = 'Extra',
  Trailer = 'Trailer',
}

// collectionType
// export enum ChannelCollectionType {
//   Series = 'Series',
//   Season = 'Season',
//   Extra = 'Extra',
//   Trailer = 'Trailer',
// }

export interface GraphicsOverlayConfig {
  url?: string;
}
export interface LiveProgramConfig {
  liveProgramIds: string[];
}
export interface LinkedChannelSettings {
  contentLinkedChannelId: string;
}
export interface ChannelContentLimits {
  maxClipDuration?: string; // a timestamp string in hh:mm:ss format eg. "00:01:30"
  maxPlaylistDuration?: string; // a timestamp string in hh:mm:ss format eg. "00:01:30"
  maxClipsPerChannel?: number;
  prefixSortOrder?: string[];
}
export interface NestedChannelConfig {
  isEnabled?: boolean; // a csv string of zip codes eg. '00000, 11111, 22222'
}
export interface GeoFencing {
  zipcodes?: string; // a csv string of zip codes eg. '00000, 11111, 22222'
}
export interface EpgConfig {
  enableEPGGeneration: boolean;
  events: EpgEvent[];
  daysToKeep: number;
  epgId?: string | null;
}
export interface EpgEvent {
  id: string;
  startDateTime: string;
  endDateTime: string;
  title: string;
  description: string;
  categories: string[];
}
export interface EPGData {
  entityType: string;
  id: string;
  targetChannelId: string | null | undefined;
  iCalendarConfig: string | null | undefined;
  isCompressed: boolean;
  entityOuId: string | null | undefined;
}
export interface ChannelLiveFillers {
  AdFiller: string[];
  BeginMomentarily: string[];
  StartupLive: string[];
  StartupRecorded: string[];
  PreRecordedToLive: string[];
  SwitchToMostRecent: string[];
  ContentRestricted: string[];
  Backstop: string[];
}

export interface ChannelFillerPool {
  itemSEClipIds?: string[];
}

export interface ChannelLiveProgram extends OUModelBase {
  liveProgramId?: string;
  name?: string;
  ouName?: string;
  channelId?: string;
  isScheduledProgram?: boolean;
  isDeleted?: boolean;
  scheduleId?: string;
  day?: number;
  startTime?: TimeSpan;
  duration?: number;
  offsetStartTime?: number;
  startupProgramClipId?: string;
  isCutToLive?: boolean;
  hasGeofencing?: boolean;
}

export interface TimeSpan {
  hh?: string;
  mm?: string;
  ss?: string;
}

export interface ChannelWithOU extends Channel, OrganizationalUnit { }

export interface ChannelPlayItem {
  title: string;
  itemId: string;
  previewUri: string;
  fileUri: string;
  tags: string[];
  attributes: string[];
  categories: string[];
  score?: number;
  duration?: number;
}

export interface AssetTransition {
  id?: string;
  assetId: string;
  transitionId: string;
  transitionType: AssetTransitionType;
  /**
   * reuse ConformStatus
   *   Pending - conform initiated
   *   Processing - conform in process. This is not implemented yet.
   *   Ready - conform finished
   *   Error - conform failed
   */
  status: ConformStatus;
  errors?: number;
}

export interface TagPrefixSortInfo {
  name: string;
  type: string;
  sortOrder: string;
}
